<template>
  <header class="ksk-header-wrap" :class="{ 'ksk-header-wrap--customizing': customizing }">
    <div class="ksk-header">
      <!-- Brand -->
      <div class="ksk-header__brand">
        <img v-if="merchantLogo" :src="merchantLogo" alt="" class="ksk-header__logo" />
        <span v-else class="ksk-header__logo ksk-header__logo--fallback material-icons-round">storefront</span>
        <span class="ksk-header__divider"></span>
        <div class="ksk-header__names">
          <span class="ksk-header__branch">{{ branchName || merchantName }}</span>
        </div>
      </div>

      <!-- KSK096: member / sign-in sits in the centre of the header on the kiosk. -->
      <div class="ksk-header__center">
        <template v-if="memberInfo">
          <!-- KSK051: the member chip is the logout button (bigger target than the old icon). -->
          <button
            v-if="showLogout"
            type="button"
            class="ksk-member ksk-member--logout"
            v-bind="kskSlot('header_member')"
            :aria-label="$t('kiosk.header.logoutAria', { name: memberName })"
            @click="logout"
          >
            <span class="ksk-member__avatar">{{ memberInitials }}</span>
            <span class="ksk-member__name">{{ memberName }}</span>
            <!-- KSK056: logout icon merged into the pill so the chip reads as tappable. -->
            <span class="material-icons-round ksk-member__exit" aria-hidden="true">logout</span>
          </button>
          <span v-else class="ksk-member" v-bind="kskSlot('header_member')">
            <span class="ksk-member__avatar">{{ memberInitials }}</span>
            <span class="ksk-member__name">{{ memberName }}</span>
          </span>
        </template>
        <KskChip v-else-if="!memberInfo && canSignIn" icon="person" interactive color-slot="header_signin" @click="signIn">
          {{ $t("kiosk.header.signIn") }}
        </KskChip>
      </div>

      <!-- Utilities -->
      <div class="ksk-header__actions">
        <KskChip
          v-if="diningLabel && (!isCheckoutPage || showDining)"
          :icon="diningOption == 1 ? 'shopping_bag' : 'restaurant'"
          :interactive="diningSwitchable"
          color-slot="header_dining"
          :aria-label="diningSwitchable ? $t('kiosk.header.changeDiningAria', { label: diningLabel }) : null"
          @click="$emit('change-dining')"
        >
          {{ diningLabel }}
          <span v-if="diningSwitchable" class="material-icons-round ksk-header__chevron">expand_more</span>
        </KskChip>
        <!-- KSK096: language toggle on every kiosk page (same list as Landing). -->
        <div v-if="isKiosk" ref="langPicker" class="ksk-header__lang">
          <KskChip
            icon="language"
            interactive
            :aria-label="$t('kiosk.header.changeLanguageAria')"
            :aria-expanded="isLanguageMenuOpen ? 'true' : 'false'"
            @click="isLanguageMenuOpen = !isLanguageMenuOpen"
          >
            {{ activeLanguage.toUpperCase() }}
          </KskChip>
          <ul v-if="isLanguageMenuOpen" class="ksk-header__lang-menu" role="listbox">
            <li
              v-for="lang in languages"
              :key="lang.code"
              role="option"
              :aria-selected="activeLanguage === lang.code"
              class="ksk-header__lang-option"
              :class="{ 'ksk-header__lang-option--active': activeLanguage === lang.code }"
              @click="selectLanguage(lang.code)"
            >
              <span>{{ lang.label }}</span>
              <span v-if="activeLanguage === lang.code" class="material-icons-round">check</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

  </header>
</template>

<script>
import { localStorageName } from "@/config/utils";
import EventBus from "vue3-eventbus";
import $ from "jquery";
import KskChip from "./ui/KskChip.vue";
import { kskSlot } from "@/config/kioskColorSlots";
import { setKioskLocale } from "@/i18n";

export default {
  name: "KioskHeader",
  emits: ["change-dining"],
  components: {
    KskChip,
  },
  props: {
    isCheckoutPage: {
      type: Boolean,
      default: false,
    },
    // The page can switch dining option (branch offers both): the dining
    // pill becomes a button that emits change-dining.
    diningSwitchable: {
      type: Boolean,
      default: false,
    },
    // KSK052: checkout shows the dining chip here too (retail keeps it in the body).
    showDining: {
      type: Boolean,
      default: false,
    },
    // false: the member chip shows the name only, without logout.
    showLogout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      merchantCode: "",
      branchCode: "",
      merchantName: "",
      merchantLogo: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO")) || "{}"
      ),
      memberInfo: JSON.parse(
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO"))
      ),
      diningOption: localStorage.getItem(localStorageName("DINING_OPTION")),
      // KSK021: the item customize sheet (ModalItemDetail) is open.
      customizing: false,
      // KSK096: same list as the Landing picker (KSK050).
      languages: [
        { code: "en", label: "English" },
        { code: "zh", label: "中文" },
        { code: "ja", label: "日本語" },
        { code: "ko", label: "한국어" },
        { code: "th", label: "ไทย" },
      ],
      isLanguageMenuOpen: false,
    };
  },
  computed: {
    // physical-branch reuses this header; the language toggle is kiosk-only.
    isKiosk() {
      return this.$route.path.includes("/kiosk");
    },
    activeLanguage() {
      return this.$i18n.locale;
    },
    branchName() {
      return this.branch?.branch_name ?? "";
    },
    // DINING_OPTION: 2 = Dine in, 1 = Takeaway (see ModalSelectDiningOption).
    diningLabel() {
      if (this.diningOption == 2) return this.$t("kiosk.header.dineIn");
      if (this.diningOption == 1) return this.$t("kiosk.header.takeaway");
      return "";
    },
    // Same gate the footer used for "Membership Sign In"; only where the
    // sign-in modal is mounted.
    canSignIn() {
      // KSK110: retail only offers staff sign-in, on Checkout.
      if (this.branch?.enable_retail_mode) {
        return !!this.branch?.staff_on_retail_mode && this.$route.name === "CheckoutPageKiosk";
      }
      return (
        !!this.branch?.membership_on_kiosk &&
        ["OrderPageKiosk", "CheckoutPageKiosk"].includes(this.$route.name)
      );
    },
    memberName() {
      return this.memberInfo?.name || this.memberInfo?.phone_number || "";
    },
    memberInitials() {
      const name = (this.memberInfo?.name || "").trim();
      if (!name) return "";
      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
    },
  },
  created() {
    this.merchantCode = this.$route.params.merchant_code;
    this.branchCode = this.$route.params.branch_code;
    this.merchantName =
      localStorage.getItem(localStorageName("MERCHANT_BRAND_NAME")) ?? "";
    this.merchantLogo =
      localStorage.getItem(localStorageName("MERCHANT_LOGO")) ?? "";
  },
  mounted() {
    EventBus.on("LogoutSuccess", () => {
      this.memberInfo = null;
      this.diningOption = localStorage.getItem(localStorageName("DINING_OPTION"));
    });

    EventBus.on("LoginSuccess", () => {
      this.memberInfo = JSON.parse(
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO")) || "null"
      );
    });

    EventBus.on("KioskCustomizeSheet", (open) => {
      this.customizing = !!open;
    });

    EventBus.on("DiningOptionChanged", () => {
      this.diningOption = localStorage.getItem(localStorageName("DINING_OPTION"));
    });

    document.addEventListener("pointerdown", this.closeLanguageMenuOutside);
  },
  beforeUnmount() {
    document.removeEventListener("pointerdown", this.closeLanguageMenuOutside);
  },
  methods: {
    kskSlot,
    logout() {
      EventBus.emit("Logout");
    },

    signIn() {
      if (this.branch?.enable_retail_mode) {
        EventBus.emit("ShowStaffSignIn");
        return;
      }
      $(".btn-show-modal-signin").click();
    },

    selectLanguage(code) {
      setKioskLocale(code);
      this.isLanguageMenuOpen = false;
    },

    closeLanguageMenuOutside(event) {
      if (this.isLanguageMenuOpen && !this.$refs.langPicker?.contains(event.target)) {
        this.isLanguageMenuOpen = false;
      }
    },
  },
};
</script>

<style scoped>
.ksk-header-wrap {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2;
  font-family: var(--ksk-font);
}
/* The customize sheet sits right below the header; the header's own actions
   (sign in, dining option …) stay inert until it closes. */
.ksk-header-wrap--customizing {
  z-index: 1061; /* above the sheet's static Bootstrap backdrop */
  pointer-events: none;
}

.ksk-header {
  height: var(--ksk-header-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ksk-space-3);
  padding: 0 var(--ksk-space-3);
  /* KSK117: Minty's "Header" background, on every page with this header. */
  background: var(--ksk-c-bg_header-accent, var(--ksk-surface));
  border-bottom: 1px solid var(--ksk-border);
  box-shadow: 0 1px 8px rgba(20, 26, 20, 0.04);
}
/* KSK063: on the kiosk, keep the sign-in / logout chip clear of the screen
   edge (physical-branch reuses this header and keeps the old padding). */
html.ksk-active .ksk-header {
  padding-right: var(--ksk-space-6);
}

/* Brand */
.ksk-header__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden; /* KSK040: shrink instead of spilling over the actions */
}
.ksk-header__logo {
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: 10px;
  object-fit: cover;
}
.ksk-header__logo--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
  font-size: 22px;
}
.ksk-header__divider {
  width: 1px;
  height: 32px;
  flex: none;
  background: var(--ksk-border);
}
.ksk-header__names {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}
.ksk-header__branch {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  font-weight: 600;
  color: var(--ksk-text);
}
.ksk-header__chevron {
  margin: 0 -4px 0 -2px;
  font-size: 24px;
  line-height: 1;
}
/* KSK096: member / sign-in. Physical-branch keeps it at the right end, after the
   dining pill (same spacing as before); the kiosk centres it (grid below). */
.ksk-header__brand {
  margin-right: auto;
}
.ksk-header__center {
  display: flex;
  align-items: center;
  order: 1;
  margin-left: calc(12px - var(--ksk-space-3));
}
/* Utilities */
.ksk-header__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
}
.ksk-member {
  height: 44px;
  display: inline-flex;
  align-items: center;
  gap: var(--ksk-space-1);
  padding: 0 var(--ksk-space-2) 0 6px;
  /* KSK063: header_member colour slot; theme values otherwise. */
  border: 1px solid var(--ksk-slot-outline, var(--ksk-border));
  border-radius: var(--ksk-radius-pill);
  background: color-mix(in srgb, var(--ksk-slot-accent, var(--ksk-primary)) 12%, #ffffff);
  white-space: nowrap;
}
.ksk-member__avatar {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--ksk-slot-accent, var(--ksk-primary));
  color: var(--ksk-on-primary);
  font-size: 14px;
  font-weight: 700;
}
.ksk-member__name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  font-weight: 600;
  color: var(--ksk-slot-font, var(--ksk-text));
}
/* KSK051: member chip as the logout button. */
.ksk-member--logout {
  position: relative;
  overflow: hidden;
  font-family: inherit;
  cursor: pointer;
  transition: transform 80ms ease-out;
}
.ksk-member__exit {
  height: 24px;
  display: flex;
  align-items: center;
  padding-left: var(--ksk-space-1);
  border-left: 1px solid var(--ksk-slot-outline, var(--ksk-border));
  font-size: 20px;
  color: var(--ksk-slot-font, var(--ksk-text-secondary));
}
.ksk-member--logout::after {
  content: "";
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
}
.ksk-member--logout:active {
  transform: scale(0.97);
}
.ksk-member--logout:active::after {
  opacity: 0.08;
}

/* KSK089: bigger header type on the kiosk (physical-branch keeps the old sizes). */
html.ksk-active .ksk-header__logo {
  width: 72px;
  height: 72px;
  border-radius: 14px;
}
html.ksk-active .ksk-header__logo--fallback {
  font-size: 40px;
}
html.ksk-active .ksk-header__divider {
  height: 56px;
}
html.ksk-active .ksk-header__branch {
  max-width: 320px;
}
html.ksk-active .ksk-member__name {
  max-width: 160px; /* KSK096: was 220px, member now shares the row centre */
}
html.ksk-active .ksk-header__branch,
html.ksk-active .ksk-member__name,
html.ksk-active .ksk-header__actions :deep(.ksk-chip--pill),
html.ksk-active .ksk-header__center :deep(.ksk-chip--pill) {
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
}
/* KSK096: chips a notch smaller (72→64px, 24→22px) so brand | member |
   dining + language fit one row. */
html.ksk-active .ksk-member__name,
html.ksk-active .ksk-header__actions :deep(.ksk-chip--pill),
html.ksk-active .ksk-header__center :deep(.ksk-chip--pill) {
  font-size: 22px;
}
html.ksk-active .ksk-header__actions :deep(.ksk-chip--pill),
html.ksk-active .ksk-header__center :deep(.ksk-chip--pill) {
  height: 64px;
  padding: 0 var(--ksk-space-2);
}
html.ksk-active .ksk-member {
  height: 64px;
  padding: 0 var(--ksk-space-2) 0 8px;
}
html.ksk-active .ksk-header__actions :deep(.ksk-chip__icon),
html.ksk-active .ksk-header__center :deep(.ksk-chip__icon),
html.ksk-active .ksk-header__chevron {
  font-size: 28px;
}
html.ksk-active .ksk-member__avatar {
  width: 48px;
  height: 48px;
  font-size: 20px;
}
html.ksk-active .ksk-member__exit {
  height: 32px;
  padding-left: var(--ksk-space-1);
  font-size: 28px;
}

/* KSK096: brand | member (true screen centre) | dining + language. Equal side
   padding so the middle column lines up with the screen centre. */
html.ksk-active .ksk-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: var(--ksk-space-2);
  padding: 0 var(--ksk-space-4);
}
html.ksk-active .ksk-header__brand {
  margin-right: 0;
}
html.ksk-active .ksk-header__center {
  order: 0;
  margin-left: 0;
}
html.ksk-active .ksk-header__actions {
  justify-self: end;
  min-width: 0;
}

/* KSK096 — language toggle + dropdown (list styled like the Landing picker). */
.ksk-header__lang {
  position: relative;
  flex: none;
}
.ksk-header__lang-menu {
  position: absolute;
  top: calc(100% + var(--ksk-space-1));
  right: 0;
  min-width: 280px;
  margin: 0;
  padding: 8px;
  list-style: none;
  border-radius: 22px;
  background: var(--ksk-surface);
  box-shadow: 0 6px 24px rgba(20, 26, 20, 0.18);
}
.ksk-header__lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  height: 72px;
  padding: 0 var(--ksk-space-3);
  border-radius: 14px;
  font-size: 26px;
  line-height: 32px;
  font-weight: 600;
  color: var(--ksk-text);
  white-space: nowrap;
  cursor: pointer;
}
.ksk-header__lang-option:active {
  background: var(--ksk-surface-muted);
}
.ksk-header__lang-option--active {
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-header__lang-option .material-icons-round {
  font-size: 32px;
}
</style>
